import React, { useEffect, useState } from 'react'
import * as api from '../../app/axios/api.jsx'

import {
  Calendar,
  Search,
  Download,
  ChevronUp,
  ChevronDown,
  Filter,
  Trash2,
  X,
  AlertTriangle,
} from 'lucide-react'

const Attendance = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedDate, setSelectedDate] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
  const [courseFilter, setCourseFilter] = useState('all')
  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    id: null,
    name: '',
  })
  const [deleting, setDeleting] = useState(false)

  // Function to get current date in YYYY-MM-DD format
  const getCurrentDate = () => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Initialize selected date to current date
  useEffect(() => {
    setSelectedDate(getCurrentDate())
  }, [])

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  // Format time for display
  const formatTime = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Get all attendance function
  const fetchAttendance = async (date = selectedDate) => {
    try {
      setLoading(true)

      const currentDate = date || getCurrentDate()

      // Make sure the date is being passed correctly
      const res = await api.getAttendance(currentDate)

      setData(res.data)

      // Mock data for demonstration - remove this when using real API
    } catch (error) {
      console.error('Error fetching attendance:', error)
      setError(error.message || 'Failed to fetch attendance data')
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  // Handle date change
  const handleDateChange = (e) => {
    const newDate = e.target.value
    setSelectedDate(newDate)
    fetchAttendance(newDate)
  }

  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }

  // Handle delete confirmation
  const handleDeleteClick = (id, name) => {
    setDeleteConfirm({ show: true, id, name })
  }

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteConfirm({ show: false, id: null, name: '' })
  }

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true)

      // Call your delete API here
      await api.deleteAttendance(deleteConfirm.id)

      // Remove the deleted item from local state
      setData((prevData) =>
        prevData.filter((item) => item.id !== deleteConfirm.id),
      )

      // Close confirmation dialog
      setDeleteConfirm({ show: false, id: null, name: '' })
    } catch (error) {
      console.error('Error deleting attendance:', error)
      // You can show an error message here
      alert('Failed to delete attendance record. Please try again.')
    } finally {
      setDeleting(false)
    }
  }

  // Sort data
  const sortedData = React.useMemo(() => {
    let sortableData = [...data]
    if (sortConfig.key) {
      sortableData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1
        }
        return 0
      })
    }
    return sortableData
  }, [data, sortConfig])

  // Filter data based on search term and course filter
  const filteredData = sortedData.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.enrollment_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.course.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.father_name.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesCourse =
      courseFilter === 'all' ||
      item.course.toLowerCase().includes(courseFilter.toLowerCase())

    return matchesSearch && matchesCourse
  })

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'S.No',
      'Name',
      'Enrollment Number',
      'Course',
      'Father Name',
      'Admission ID',
      'Date',
      'Time',
    ]
    const csvContent = [
      headers.join(','),
      ...filteredData.map((item, index) =>
        [
          index + 1,
          item.name,
          item.enrollment_number,
          item.course,
          item.father_name,
          item.admission_id,
          formatDate(item.created_at),
          formatTime(item.created_at),
        ].join(','),
      ),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `attendance_${selectedDate}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  useEffect(() => {
    fetchAttendance()
  }, [])

  if (loading) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <div className='flex flex-col items-center'>
          <div className='w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin'></div>
          <p className='mt-4 text-gray-600 font-medium'>
            Loading attendance data...
          </p>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <div className='flex flex-col items-center'>
          <div className='text-red-500 text-xl mb-4'>Error</div>
          <p className='text-gray-600 mb-4'>{error}</p>
          <button
            onClick={() => fetchAttendance()}
            className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='min-h-screen bg-gray-50'>
      {/* Header */}
      <div className='bg-white shadow-sm border-b'>
        <div className='max-w-full mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='py-4'>
            <h1 className='text-xl sm:text-2xl font-bold text-gray-900 mb-4'>
              Attendance Management - {formatDate(selectedDate)}
            </h1>

            {/* Controls */}
            <div className='flex flex-col space-y-4 lg:space-y-0 lg:flex-row lg:items-center lg:justify-between'>
              <div className='flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-4'>
                {/* Date Picker */}
                <div className='flex items-center gap-2'>
                  <Calendar className='h-5 w-5 text-gray-500 flex-shrink-0' />
                  <input
                    type='date'
                    value={selectedDate}
                    onChange={handleDateChange}
                    className='px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'
                  />
                </div>

                {/* Search */}
                <div className='flex items-center gap-2'>
                  <Search className='h-5 w-5 text-gray-500 flex-shrink-0' />
                  <input
                    type='text'
                    placeholder='Search records...'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className='px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-64 text-sm'
                  />
                </div>

                {/* Course Filter */}
                <div className='flex items-center gap-2'>
                  <Filter className='h-5 w-5 text-gray-500 flex-shrink-0' />
                  <select
                    value={courseFilter}
                    onChange={(e) => setCourseFilter(e.target.value)}
                    className='px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white'
                  >
                    <option value='all'>All Courses</option>
                    <option value='summer training'>Summer Training</option>
                    <option value='cfm'>CFM</option>
                    <option value='full stack'>Full Stack</option>
                  </select>
                </div>
              </div>

              {/* Export Button */}
              <div className='flex justify-end'>
                <button
                  onClick={exportToCSV}
                  className='px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center gap-2 text-sm'
                >
                  <Download className='h-4 w-4' />
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className='max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6'>
        {filteredData && filteredData.length > 0 ? (
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
            {/* Table Stats */}
            <div className='px-4 sm:px-6 py-3 bg-gray-50 border-b border-gray-200'>
              <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0'>
                <span className='text-sm font-medium text-gray-700'>
                  Total Records: {filteredData.length}
                </span>
                {courseFilter !== 'all' && (
                  <span className='text-sm text-gray-500'>
                    Filtered by:{' '}
                    {courseFilter === 'summer training'
                      ? 'Summer Training'
                      : courseFilter === 'cfm'
                        ? 'CFM'
                        : 'Full Stack'}
                  </span>
                )}
              </div>
            </div>

            {/* Table */}
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      S.No
                    </th>
                    {[
                      { key: 'name', label: 'Name' },
                      { key: 'enrollment_number', label: 'Enrollment Number' },
                      { key: 'course', label: 'Course' },
                      { key: 'father_name', label: 'Father Name' },
                      { key: 'admission_id', label: 'Admission ID' },
                    ].map(({ key, label }) => (
                      <th
                        key={key}
                        className='px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100'
                        onClick={() => handleSort(key)}
                      >
                        <div className='flex items-center gap-1'>
                          {label}
                          {sortConfig.key === key &&
                            (sortConfig.direction === 'asc' ? (
                              <ChevronUp className='h-4 w-4' />
                            ) : (
                              <ChevronDown className='h-4 w-4' />
                            ))}
                        </div>
                      </th>
                    ))}
                    <th className='px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Date
                    </th>
                    <th className='px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Time
                    </th>
                    <th className='px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {filteredData.map((row, index) => (
                    <tr key={row.id} className='hover:bg-gray-50'>
                      <td className='px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900'>
                        {index + 1}
                      </td>
                      <td className='px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {row.name}
                      </td>
                      <td className='px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {row.enrollment_number}
                      </td>
                      <td className='px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                        <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800'>
                          {row.course}
                        </span>
                      </td>
                      <td className='px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {row.father_name}
                      </td>
                      <td className='px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {row.admission_id}
                      </td>
                      <td className='px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {formatDate(row.created_at)}
                      </td>
                      <td className='px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {formatTime(row.created_at)}
                      </td>
                      <td className='px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                        <button
                          onClick={() => handleDeleteClick(row.id, row.name)}
                          className='text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded-md transition-colors duration-200'
                          title='Delete Record'
                        >
                          <Trash2 className='h-4 w-4' />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-8 sm:p-12 text-center'>
            <div className='text-gray-500 text-lg mb-2'>
              No attendance records found
            </div>
            <p className='text-gray-400 text-sm sm:text-base'>
              {searchTerm || courseFilter !== 'all'
                ? `No records match your current filters`
                : `No attendance data found for ${formatDate(selectedDate)}`}
            </p>
            {(searchTerm || courseFilter !== 'all') && (
              <div className='mt-4 space-x-2'>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className='text-blue-600 hover:text-blue-800 text-sm'
                  >
                    Clear search
                  </button>
                )}
                {courseFilter !== 'all' && (
                  <button
                    onClick={() => setCourseFilter('all')}
                    className='text-blue-600 hover:text-blue-800 text-sm'
                  >
                    Clear filter
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-lg shadow-xl max-w-md w-full mx-4'>
            <div className='flex items-center justify-between p-4 border-b border-gray-200'>
              <div className='flex items-center gap-3'>
                <div className='flex-shrink-0'>
                  <AlertTriangle className='h-6 w-6 text-red-600' />
                </div>
                <h3 className='text-lg font-semibold text-gray-900'>
                  Confirm Delete
                </h3>
              </div>
              <button
                onClick={handleDeleteCancel}
                className='text-gray-400 hover:text-gray-600 p-1 rounded-md'
              >
                <X className='h-5 w-5' />
              </button>
            </div>

            <div className='p-4'>
              <p className='text-sm text-gray-600 mb-4'>
                Are you sure you want to delete the attendance record for{' '}
                <span className='font-semibold text-gray-900'>
                  {deleteConfirm.name}
                </span>
                ? This action cannot be undone.
              </p>

              <div className='flex flex-col sm:flex-row gap-3 sm:justify-end'>
                <button
                  onClick={handleDeleteCancel}
                  className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                  className='px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2'
                >
                  {deleting ? (
                    <>
                      <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className='h-4 w-4' />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Attendance
